'use client';

import type { ProductAttributesGroupT } from '../../../../../type/product/productAttributes';
import type { ProductToCompareT } from '../../../../../type/product/productComparison';

import {
  ProductComparison,
  ProductComparisonGrid,
  ProductComparisonGridVariant,
  ProductComparisonSentinel,
  productComparisonColumnStyle,
} from '@vs-shop/commerce-ui/productComparison';
import { useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import { useDragAndDropProductsComparison } from '../../../../../hook/useDragAndDropProductsComparison';

import ProductComparisonStickyHeader from '../ProductComparisonStickyHeader';
import ProductComparisonActionsRow from './header/ProductComparisonActionsRow';
import ProductComparisonHeaderAddToCartRow from './header/ProductComparisonHeaderAddToCartRow';
import ProductComparisonProductHeaderRows from './header/ProductComparisonProductHeaderRows';
import ProductComparisonAttributeGroupRow from './ProductComparisonAttributeGroupRow';
import ProductComparisonTableSummaryRow from './ProductComparisonTableSummaryRow';

type Props = {
  products: readonly ProductToCompareT[];
  attributeGroups: readonly ProductAttributesGroupT[];
};

export default function ProductComparisonTable({ products, attributeGroups }: Props) {
  const [collapsibleState, setCollapsibleState] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(attributeGroups.map((g) => [g.group.id, true]))
  );

  const {
    draggingIndex,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDrop,
  } = useDragAndDropProductsComparison(products);

  // Detect when header is ABOVE the viewport to show the STICKY CONDENSED  HEADER
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const { isIntersecting: _isIntersecting, ref: headerSentinelRef } = useIntersectionObserver({
    threshold: 0,
    rootMargin: '0px',
    freezeOnceVisible: false,
    initialIsIntersecting: true,
    onChange: (isIntersecting, entry) => {
      if (!isIntersecting && entry.boundingClientRect.top < 0) {
        setShowStickyHeader(true);
      } else if (isIntersecting) {
        setShowStickyHeader(false);
      }
    },
  });

  const toggleGroup = (groupId: string) => {
    setCollapsibleState((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <>
      {/* STICKY HEADER */}
      <ProductComparisonStickyHeader
        products={products}
        isVisible={showStickyHeader}
        draggingIndex={draggingIndex}
        dragOverIndex={dragOverIndex}
      />

      <ProductComparison>
        <ProductComparisonGrid
          variant={ProductComparisonGridVariant.TABLE}
          style={productComparisonColumnStyle(products.length)}
        >
          {/* HEADER */}
          <ProductComparisonActionsRow
            products={products}
            draggingIndex={draggingIndex}
            dragOverIndex={dragOverIndex}
            onDragStartAction={handleDragStart}
            onDragOverAction={handleDragOver}
            onDragEndAction={handleDragEnd}
            onDropAction={handleDrop}
          />
          <ProductComparisonProductHeaderRows
            products={products}
            draggingIndex={draggingIndex}
            dragOverIndex={dragOverIndex}
          />
          <ProductComparisonHeaderAddToCartRow
            products={products}
            draggingIndex={draggingIndex}
            dragOverIndex={dragOverIndex}
          />

          {/* Sentinel element to detect when header is out of view */}
          <ProductComparisonSentinel ref={headerSentinelRef} />

          {/* ATTRIBUTES */}
          {attributeGroups.map((group) => (
            <ProductComparisonAttributeGroupRow
              key={group.group.id}
              group={group.group}
              products={products}
              attributes={group.attributes}
              isOpen={collapsibleState[group.group.id] ?? true}
              onToggleAction={() => {
                toggleGroup(group.group.id);
              }}
            />
          ))}

          <ProductComparisonTableSummaryRow products={products} />
        </ProductComparisonGrid>
      </ProductComparison>
    </>
  );
}
